import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Users, Eye } from 'lucide-react';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { Database } from '@/integrations/supabase/types';


type Membership = Database['public']['Tables']['memberships']['Row'];

interface MemberWithProfile extends Membership {
  profiles?: {
    id: string;
    email: string | null;
    display_name: string | null;
  };
}

export function MemberSelector() {
  const { supportSession, switchToMember, switchToShopView } = useSupportMode();
  const [members, setMembers] = useState<MemberWithProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supportSession?.targetShopId) return;

    const loadMembers = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('memberships')
        .select(`
          *,
          profiles:user_id (
            id,
            email,
            display_name
          )
        `)
        .eq('shop_id', supportSession.targetShopId)
        .eq('status', 'active')
        .order('role');

      if (!error && data) {
        setMembers(data as any);
      }
      setLoading(false);
    };

    loadMembers();
  }, [supportSession?.targetShopId]);

  if (!supportSession) return null;

  const isUserMode = supportSession.mode === 'USER';
  const currentUserId = supportSession.targetMembership?.user_id;

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <Users className="h-5 w-5" />
        <h3 className="font-semibold">View as Member</h3>
      </div>

      {isUserMode && (
        <Button
          variant="outline"
          size="sm"
          onClick={switchToShopView}
          className="mb-3 w-full"
        >
          Return to Shop View
        </Button>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading members...</p>
      ) : (
        <div className="space-y-2">
          {members.map((member) => {
            const isActive = isUserMode && member.user_id === currentUserId;
            return (
              <div
                key={member.id}
                className={`flex items-center justify-between p-2 rounded-lg border ${
                  isActive ? 'bg-primary/10 border-primary' : 'bg-background'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {member.profiles?.display_name || member.profiles?.email || 'Unknown'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {member.role}
                  </p>
                </div>
                {!isActive && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => switchToMember(member.user_id)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                )}
                {isActive && (
                  <span className="text-xs font-semibold text-primary">
                    Active
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
